import type { ReadValuationReportMode } from '@immo/api-contract';
import { apiClient } from '@/lib/auth/api-client';

/**
 * Mint a short-lived presigned GET URL for one of a request's attachments on the
 * operator surface. Mirrors the agent-side `resolveValuationReportUrl`: `preview`
 * yields an inline URL for an in-app Vorschau, `download` yields an attachment
 * URL the browser saves under the original filename. The URL is minted per call
 * and never stored. Returns the URL on ok, or `null` when the attachment can no
 * longer be read (request or document gone, bytes missing).
 */
export async function resolveValuationDocumentUrl(
  valuationRequestId: string,
  documentId: string,
  mode: ReadValuationReportMode,
): Promise<string | null> {
  const result = await apiClient.adminValuationRequests.readDocument({
    pathParams: { id: valuationRequestId, documentId },
    query: { mode },
  });

  return result.ok ? result.body.url : null;
}

/**
 * Open one attachment inline in a new tab (Vorschau). Returns `true` on success,
 * or `false` when the URL can no longer be minted so the caller can surface an
 * error.
 */
export async function previewValuationDocument(valuationRequestId: string, documentId: string): Promise<boolean> {
  const url = await resolveValuationDocumentUrl(valuationRequestId, documentId, 'preview');

  if (url === null) return false;

  window.open(url, '_blank', 'noopener');

  return true;
}

/**
 * Trigger a byte-for-byte download of one attachment under its original filename.
 * Clicks a programmatic anchor — the S3 target carries an `attachment`
 * content-disposition, so we deliberately do NOT set the `download` attribute
 * (ignored cross-origin) nor blob-fetch (CORS). Returns `true` on success, or
 * `false` when the URL can no longer be minted.
 */
export async function downloadValuationDocument(valuationRequestId: string, documentId: string): Promise<boolean> {
  const url = await resolveValuationDocumentUrl(valuationRequestId, documentId, 'download');

  if (url === null) return false;

  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.rel = 'noopener';
  anchor.click();

  return true;
}
